/**
 * Attack Surface — physical access points, network entry points,
 * diagnostic ports, supply-chain attack vectors, asset candidates.
 * Pulls from `formatted.attack_surface`. Always renders.
 */
import React from 'react';
import { SectionCard, SubSection, ObjectListField, type SectionProps } from './parts';

const PATH = ['attack_surface'];

const yesNo = (v) => (v === true ? 'true' : v === false ? 'false' : '');
const joinList = (v) => (Array.isArray(v) ? v.join(', ') : v);

export default function AttackSurface({ data, isEditing, onChange }: SectionProps) {
  const as = data ?? {};
  const physical = as.physical_access_points?.points ?? [];
  const network = as.network_entry_points?.points ?? [];
  const diagnostic = as.diagnostic_ports?.ports ?? [];
  const vectors = as.supply_chain_attack_vectors?.vectors ?? [];
  const candidates = as.asset_candidates?.candidates ?? [];

  const physColumns = [
    { key: 'access_point_id', label: 'ID' },
    { key: 'location', label: 'Location' },
    { key: 'access_type', label: 'Access Type' },
    { key: 'access_level_required', label: 'Access Required' },
    { key: 'authentication_needed', label: 'Auth Needed', render: yesNo },
    { key: 'security_controls', label: 'Security Controls', render: joinList },
    { key: 'accessible_functions', label: 'Functions', render: joinList },
  ];
  const netColumns = [
    { key: 'entry_point_id', label: 'ID' },
    { key: 'interface_name', label: 'Interface' },
    { key: 'protocol', label: 'Protocol' },
    { key: 'threat_level', label: 'Threat Level' },
    { key: 'external_facing', label: 'External', render: yesNo },
    { key: 'attack_surface_notes', label: 'Notes', render: joinList },
    { key: 'security_controls', label: 'Security Controls', render: joinList },
  ];
  const diagColumns = [
    { key: 'port_type', label: 'Type' },
    { key: 'location', label: 'Location' },
    { key: 'accessible_functions', label: 'Functions', render: joinList },
    { key: 'authentication_required', label: 'Auth Required', render: yesNo },
    { key: 'authorization_levels', label: 'Authorization', render: joinList },
    { key: 'security_risks', label: 'Risks', render: joinList },
    { key: 'mitigation_controls', label: 'Mitigations', render: joinList },
  ];
  const vectorColumns = [
    { key: 'vector_description', label: 'Vector' },
    { key: 'affected_phase', label: 'Phase' },
    { key: 'external_parties', label: 'External Parties', render: joinList },
    { key: 'risk_level', label: 'Risk Level' },
    { key: 'mitigation_measures', label: 'Mitigations', render: joinList },
  ];
  const candidateColumns = [
    { key: 'name', label: 'Candidate' },
    { key: 'type', label: 'Type' },
    { key: 'description', label: 'Description' },
    { key: 'related_components', label: 'Components', render: joinList },
    { key: 'potential_value', label: 'Value' },
    { key: 'external_exposure', label: 'External Exposure', render: yesNo },
    { key: 'data_sensitivity_flag', label: 'Data Sensitivity', render: yesNo },
    { key: 'critical_function_flag', label: 'Critical Function', render: yesNo },
  ];

  return (
    <SectionCard title="Attack Surface" id="attack_surface">
      <SubSection title="Physical access points" count={physical.length}>
        <ObjectListField columns={physColumns} rows={physical} isEditing={isEditing} onChange={onChange} path={[...PATH, 'physical_access_points', 'points']} />
      </SubSection>

      <SubSection title="Network entry points" count={network.length}>
        <ObjectListField columns={netColumns} rows={network} isEditing={isEditing} onChange={onChange} path={[...PATH, 'network_entry_points', 'points']} />
      </SubSection>

      <SubSection title="Diagnostic ports" count={diagnostic.length}>
        <ObjectListField columns={diagColumns} rows={diagnostic} isEditing={isEditing} onChange={onChange} path={[...PATH, 'diagnostic_ports', 'ports']} />
      </SubSection>

      <SubSection title="Supply chain attack vectors" count={vectors.length}>
        <ObjectListField columns={vectorColumns} rows={vectors} isEditing={isEditing} onChange={onChange} path={[...PATH, 'supply_chain_attack_vectors', 'vectors']} />
      </SubSection>

      <SubSection title="Asset candidates" count={candidates.length}>
        <ObjectListField columns={candidateColumns} rows={candidates} isEditing={isEditing} onChange={onChange} path={[...PATH, 'asset_candidates', 'candidates']} />
      </SubSection>
    </SectionCard>
  );
}
